import * as React from "react"

import { cn } from "@/lib/utils"
import { toneClass } from "@/lib/status"
import { toDateInputValue } from "@/lib/format"
import { buildTimeScale, formatDayLabel, formatDayRange, rangeForDates, toDay } from "@/lib/gantt"
import { useIsPhone } from "@/hooks/useMediaQuery"

/*
  Gantt — time axis (week / month / quarter), rows with bars (plan / actual / late / proposed), milestone
  diamonds, a today line, dated markers (planned handover…), dependency arrows, horizontal scroll with a sticky
  label column. For 10 #2 (real Gantt), #3 (auto-plan, hatched proposals), #4 (milestones lane), #8 (deps).

    <Gantt
      zoom="month" today="2026-09-30" labelHeader="Stage & task" ariaLabel="LIV Waterside timeline"
      markers={[
        { key: 'handover', date: '2026-08-13', label: 'Planned handover · 13 Aug', tone: 'red', dashed: true,
          shadeTo: 'today' },                                       // pink "past the planned date" band
        { key: 'proposed', date: '2026-11-19', label: 'Proposed handover · 19 Nov', tone: 'navy', dashed: true },
      ]}
      sections={[
        { key: 'ms', label: 'Milestones', meta: 'invoices, VO approvals, retention', rows: [
          { id: 'billing', label: 'Billing & approvals', sublabel: '4 invoices · 4 VOs',
            milestones: [{ id: 'inv-482', date: '2026-09-02', label: '482 · Delivery', tone: 'navy' },
                         { id: 'vo-3', date: '2026-08-20', label: 'VO-003', tone: 'violet' }] } ] },
        { key: 'stages', label: 'Delivery stages', meta: '2 of 9 approved', rows: [
          { id: 's3', label: 'Material Submittal', marker: '03', dot: 'gray',
            bars: [{ id: 'b3', start: '2026-10-01', end: '2026-10-08', kind: 'proposed' }] },
          { id: 's4', label: 'Technical Submittal', marker: '04',
            bars: [{ id: 'b4', start: '2026-10-08', end: '2026-10-15', kind: 'proposed' }] },
          { id: 's1', label: 'Received Drawings', note: 'Approved · no dates recorded' },
        ] },
      ]}
      dependencies={[{ from: 'b3', to: 'b4' }, { from: 'b4', to: 'b9', critical: true }]}
      onBarClick={(bar, row) => openStage(row.id)} />

  Row: { id, label, sublabel, marker (e.g. '03', mono), dot (tone), indent (0-2), labelAddon (node; the name keeps
    priority, the addon gives way), note (text in the track when there are no bars), bars, milestones, height,
    lines (1 | 2: two milestone lines in one row, e.g. invoices above, VO approvals below — each milestone picks
    its `line`), leadChips ([node per line] — sticky at the start of the lane, e.g. "‹ 2 earlier"), testId }.
  Bar: { id, start, end (inclusive), kind 'plan' | 'actual' | 'done' | 'late' | 'proposed', label (default the
    date range, shown after the bar; `labelInside` to put it inside), progress (0–100, an actual fill inside a
    plan bar), tone (overrides the kind's colour), title }.
  Milestone: { id, date, label, tone (navy), shape 'diamond' (default) | 'dot', line (0 | 1 with row.lines 2), mono }.
  Marker: { key, date, label, tone ('navy'), dashed, shadeTo (date | 'today') }.
  Props: rows or sections, zoom, start / end (default: all dates ± padding), today (default: today in the company
  time zone, Admin › Settings;
  `false` hides the line), labelHeader, labelWidth (240; 150 on phones), rowHeight (40), markers, dependencies,
  onBarClick, scrollToToday (true), emptyText, ariaLabel, className, testId ('gantt').
  Keyboard: the chart scrolls with ←/→ when focused; bars with onBarClick are buttons (Tab, Enter).
*/

const BAR_KIND = {
  plan: "border border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink",
  actual: "bg-brand text-brand-fg",
  done: "bg-brand text-brand-fg",
  late: "bg-status-red text-white",
  proposed: "ui-gantt-hatch text-brand-ink",
}
const KIND_WORD = { plan: "planned", actual: "actual", done: "done", late: "late", proposed: "proposed" }
const TONE_FILL = {
  navy: "var(--status-navy)", red: "var(--status-red)", amber: "var(--status-amber)", green: "var(--status-green)",
  blue: "var(--status-blue)", violet: "var(--status-violet)", teal: "var(--status-teal)", gray: "var(--status-gray)",
  lime: "var(--status-lime)",
}

const HEADER_H = 34
const GROUP_H = 24
const MARKER_H = 30
const SECTION_H = 30

function collectDates(rows, markers, today) {
  const dates = []
  rows.forEach((row) => {
    ;(row.bars || []).forEach((bar) => dates.push(bar.start, bar.end))
    ;(row.milestones || []).forEach((milestone) => dates.push(milestone.date))
  })
  ;(markers || []).forEach((marker) => dates.push(marker.date))
  if (today) dates.push(today)
  return dates
}

function useLayout(sections, rows, rowHeight) {
  return React.useMemo(() => {
    const list = sections ? sections : [{ key: "__all", rows: rows || [] }]
    const lines = []
    let y = 0
    list.forEach((section) => {
      if (section.label) {
        lines.push({ type: "section", key: `section-${section.key}`, section, y, height: SECTION_H })
        y += SECTION_H
      }
      ;(section.rows || []).forEach((row) => {
        lines.push({ type: "row", key: `row-${row.id}`, row, y, height: row.height || rowHeight })
        y += row.height || rowHeight
      })
    })
    return { lines, height: y, rows: lines.filter((line) => line.type === "row").map((line) => line.row) }
  }, [sections, rows, rowHeight])
}

function todayIso() {
  // today in the company time zone (Admin › Settings › Workspace defaults; GST by default)
  return toDateInputValue(new Date())
}

function barText(row, bar, span) {
  const range = formatDayRange(span.startDay, span.endDay)
  const kind = KIND_WORD[bar.kind] || KIND_WORD.plan
  const progress = Number.isFinite(Number(bar.progress)) && bar.progress !== undefined ? `, ${Math.round(bar.progress)}% done` : ""
  return `${row.label}: ${bar.label && bar.label !== range ? `${bar.label}, ` : ""}${kind} ${range}${progress}`
}

function GanttBar({ row, bar, span, top, height, onBarClick }) {
  const kind = BAR_KIND[bar.kind] ? bar.kind : "plan"
  const text = barText(row, bar, span)
  const label = bar.label ?? formatDayRange(span.startDay, span.endDay).replace(/ \d{4}$/, "")
  const inside = bar.labelInside && span.width >= 56
  const style = { left: span.x, width: span.width, top, height }
  if (bar.tone && kind !== "proposed") style.background = TONE_FILL[bar.tone]
  const common = {
    className: cn(
      "absolute z-[1] flex items-center overflow-hidden rounded-[6px] text-[11.5px] font-semibold",
      BAR_KIND[kind],
      onBarClick && "cursor-pointer transition-[filter,box-shadow] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
    ),
    style,
    title: bar.title || text,
    "data-testid": bar.testId || `gantt-bar-${bar.id}`,
    "data-kind": kind,
  }
  const progress = Number(bar.progress)
  const fill = Number.isFinite(progress) && bar.progress !== undefined ? (
    <span
      className="absolute inset-y-0 left-0 bg-brand"
      style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
      aria-hidden="true"
    />
  ) : null
  const content = (
    <>
      {fill}
      {inside ? <span className="relative truncate px-2">{label}</span> : null}
    </>
  )
  return (
    <>
      {onBarClick ? (
        <button type="button" {...common} aria-label={text} onClick={() => onBarClick(bar, row)}>{content}</button>
      ) : (
        <span role="img" aria-label={text} {...common}>{content}</span>
      )}
      {!inside && label ? (
        // a surface chip: dashed marker lines (planned / proposed handover) pass behind the date, not through it
        <span
          className="pointer-events-none absolute z-[2] whitespace-nowrap rounded-[4px] bg-surface px-1 text-[12px] text-ink-2"
          style={{ left: span.x + span.width + 2, top, height, lineHeight: `${height}px` }}
          aria-hidden="true"
        >
          {label}
        </span>
      ) : null}
    </>
  )
}

function GanttMilestone({ row, milestone, x, rowHeight, center: centerProp, labelRoom = Infinity }) {
  const tone = milestone.tone || "navy"
  const size = 11
  const center = centerProp ?? rowHeight / 2
  const text = `${row.label}: ${milestone.label ? `${milestone.label}, ` : ""}${formatDayLabel(toDay(milestone.date), { withYear: true })}`
  return (
    <>
      <span
        role="img"
        aria-label={text}
        title={text}
        data-testid={`gantt-milestone-${milestone.id}`}
        className={cn("absolute z-[1]", milestone.shape === "dot" ? "rounded-full" : "rotate-45 rounded-[2px]")}
        style={{ left: x - size / 2, top: center - size / 2, width: size, height: size, background: TONE_FILL[tone] || TONE_FILL.navy }}
      />
      {milestone.label && labelRoom >= 36 ? (
        <span
          className={cn(
            "pointer-events-none absolute z-[1] truncate whitespace-nowrap rounded-md border border-line bg-surface px-1.5 text-[11.5px] font-semibold text-ink-2",
            milestone.mono && "font-mono"
          )}
          style={{ left: x + 9, top: center - 10, height: 20, lineHeight: "18px", maxWidth: Number.isFinite(labelRoom) ? labelRoom : undefined }}
          aria-hidden="true"
        >
          {milestone.label}
        </span>
      ) : null}
    </>
  )
}

function RowLabel({ row }) {
  return (
    <div className="flex min-w-0 items-center gap-2" style={{ paddingLeft: (row.indent || 0) * 16 }}>
      {row.dot ? <span className={cn("ui-tone-dot", toneClass(row.dot))} aria-hidden="true" /> : null}
      {row.marker ? <span className="shrink-0 font-mono text-[11.5px] text-ink-3">{row.marker}</span> : null}
      {/* the name keeps its room (it shrinks 10× slower); the addon gives way and is clipped first */}
      <span className="flex min-w-0 shrink-[0.1] flex-col">
        <span className="truncate text-[13px] font-medium text-ink">{row.label}</span>
        {row.sublabel ? <span className="truncate text-[11.5px] text-ink-3">{row.sublabel}</span> : null}
      </span>
      {row.labelAddon ? <span className="ml-auto flex min-w-0 shrink justify-end overflow-hidden">{row.labelAddon}</span> : null}
    </div>
  )
}

/** Milestones by line: one line (centred) or two (row.lines 2: line 0 above the centre, line 1 below). */
function milestoneLines(row, height) {
  const milestones = row.milestones || []
  if (row.lines !== 2) return [{ center: height / 2, milestones }]
  return [
    { center: height / 2 - 11, milestones: milestones.filter((milestone) => (milestone.line || 0) === 0) },
    { center: height / 2 + 11, milestones: milestones.filter((milestone) => (milestone.line || 0) === 1) },
  ]
}

let measureCanvas = null
function textWidth(text) {
  // jsdom (tests) has no canvas; estimating there avoids its "not implemented" error
  if (typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent || "")) return text.length * 6.4
  try {
    measureCanvas = measureCanvas || document.createElement("canvas")
    const ctx = measureCanvas.getContext && measureCanvas.getContext("2d")
    if (ctx) {
      ctx.font = "700 11.5px Manrope, system-ui, sans-serif"
      // + the app's word-spacing (0.1em, see index.css) which canvas does not apply, + a little slack
      return ctx.measureText(text).width * 1.04 + (text.split(" ").length - 1) * 1.2
    }
  } catch {
    // no canvas (tests): estimate below
  }
  return text.length * 6.4
}

/** Left edge (and max width) of each marker chip: centred on its date, then nudged so chips do not overlap. */
function layoutChips(list, scale) {
  const GAP = 4
  const items = list
    .map((marker) => {
      const center = scale.toX(marker.date) + (marker.today ? scale.pxPerDay / 2 : 0)
      const width = Math.ceil(textWidth(marker.label) + 14 + (marker.today ? 0 : 2))
      return { key: marker.key, center, width, x: center - width / 2 }
    })
    .sort((a, b) => a.center - b.center)
  let cursor = 4
  items.forEach((item) => {
    item.x = Math.max(item.x, cursor)
    cursor = item.x + item.width + GAP
  })
  let limit = scale.width - 4
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i]
    if (item.x + item.width > limit) item.x = limit - item.width
    limit = item.x - GAP
  }
  const result = {}
  items.forEach((item) => {
    const x = Math.max(0, item.x)
    result[item.key] = { x, width: Math.max(40, item.width - (x - item.x)) }
  })
  return result
}

/**
 * Arrow from the end of bar `from` to the start of bar `to`. It leaves from the underside (or top) of the bar's
 * last few pixels, so it never runs through the date label written after the bar.
 */
function dependencyPath(from, to) {
  const down = to.y >= from.y
  const sx = from.x + from.width - Math.min(6, from.width / 2)
  const sy = from.y + (down ? from.half : -from.half)
  const tx = to.x
  const ty = to.y
  if (tx >= sx + 10) return `M ${sx} ${sy} V ${ty} H ${tx - 2}`
  // the next bar starts before this one ends: drop to the row boundary, go back left, then in from the left
  const boundary = down ? ty - to.rowHeight / 2 : ty + to.rowHeight / 2
  return `M ${sx} ${sy} V ${boundary} H ${tx - 10} V ${ty} H ${tx - 2}`
}

function Gantt({
  rows,
  sections,
  zoom = "month",
  start,
  end,
  today,
  labelHeader = "",
  labelWidth,
  rowHeight = 40,
  markers = [],
  dependencies = [],
  onBarClick,
  scrollToToday = true,
  emptyText = "Nothing has dates yet.",
  ariaLabel = "Timeline",
  className,
  testId = "gantt",
}) {
  const isPhone = useIsPhone()
  const width = labelWidth ?? (isPhone ? 150 : 240)
  const layout = useLayout(sections, rows, rowHeight)
  const todayValue = today === false ? null : (today || todayIso())
  const range = React.useMemo(() => {
    if (start && end) return { start, end }
    const auto = rangeForDates(collectDates(layout.rows, markers, todayValue), { zoom, today: todayValue || undefined })
    return { start: start || auto.start, end: end || auto.end }
  }, [start, end, layout.rows, markers, todayValue, zoom])
  const scroller = React.useRef(null)
  const [viewport, setViewport] = React.useState(0)
  React.useEffect(() => {
    const box = scroller.current
    if (!box) return undefined
    const measure = () => setViewport(box.clientWidth)
    measure()
    if (typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => observer.disconnect()
  }, [])
  const scale = React.useMemo(() => {
    const base = buildTimeScale({ start: range.start, end: range.end, zoom })
    const available = viewport - width - 2
    // a short range stretches to fill the card instead of leaving an empty strip on the right
    return available > base.width
      ? buildTimeScale({ start: range.start, end: range.end, zoom, pxPerDay: available / base.days })
      : base
  }, [range.start, range.end, zoom, viewport, width])

  const markerList = React.useMemo(() => {
    const list = (markers || []).filter((marker) => scale.contains(marker.date))
    if (todayValue && scale.contains(todayValue)) {
      list.push({ key: "__today", date: todayValue, label: `Today · ${formatDayLabel(toDay(todayValue))}`, tone: "red", today: true })
    }
    return list
  }, [markers, scale, todayValue])
  const chipPositions = React.useMemo(() => layoutChips(markerList, scale), [markerList, scale])

  const headerH = HEADER_H + (scale.groups.length ? GROUP_H : 0)
  const markerRowH = markerList.length ? MARKER_H : 0

  const barH = Math.max(14, Math.round(rowHeight * 0.45))

  // Positions of every bar for dependency arrows.
  const barBoxes = React.useMemo(() => {
    const boxes = {}
    layout.lines.forEach((line) => {
      if (line.type !== "row") return
      ;(line.row.bars || []).forEach((bar) => {
        const span = scale.span(bar.start, bar.end)
        if (span) boxes[bar.id] = { x: span.x, width: span.width, y: line.y + line.height / 2, half: barH / 2, rowHeight: line.height }
      })
    })
    return boxes
  }, [layout.lines, scale, barH])

  React.useEffect(() => {
    if (!scrollToToday || !scroller.current || !todayValue) return
    const x = scale.toX(todayValue)
    if (x === null) return
    const box = scroller.current
    const visible = box.clientWidth - width
    // only when today is off screen: bring it to 60% of the visible timeline (the recent past stays in view)
    if (visible > 0 && x > visible - 40) box.scrollLeft = Math.max(0, x - visible * 0.6)
  }, [scale, scrollToToday, todayValue, width])

  const onKeyDown = (event) => {
    if (!scroller.current || event.target !== scroller.current) return
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault()
      scroller.current.scrollLeft += (event.key === "ArrowRight" ? 1 : -1) * 120
    }
  }

  const totalWidth = width + scale.width
  const hasRows = layout.rows.length > 0

  return (
    <div className={cn("w-full min-w-0 overflow-hidden rounded-card border border-line bg-surface [contain:inline-size]", className)} data-testid={testId}>
      <div
        ref={scroller}
        className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        role="region"
        aria-label={ariaLabel}
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        <div className="relative" style={{ width: totalWidth, minWidth: "100%" }}>
          {/* Header: month row (week zoom) + ticks */}
          <div className="flex border-b border-line bg-surface-2" style={{ height: headerH }}>
            <div
              className="sticky left-0 z-[3] flex shrink-0 items-end border-r border-line bg-surface-2 px-3 pb-2 text-[12px] font-semibold text-ink-3"
              style={{ width }}
            >
              {labelHeader}
            </div>
            <div className="relative shrink-0" style={{ width: scale.width }} aria-hidden="true">
              {scale.groups.map((group) => (
                // overflow: clip (not hidden) keeps the box from becoming a scroll container, so the label inside can
                // stick to the left edge of the visible axis until its month scrolls away
                <div
                  key={group.key}
                  className="absolute top-0 whitespace-nowrap border-l border-line text-[11.5px] font-bold text-ink-2 [overflow:clip]"
                  style={{ left: group.x, width: group.width, height: GROUP_H, lineHeight: `${GROUP_H}px` }}
                  data-testid="gantt-group"
                >
                  <span className="sticky inline-block max-w-full truncate px-2 align-top" style={{ left: width }}>{group.label}</span>
                </div>
              ))}
              {scale.ticks.map((tick) => (
                <div
                  key={tick.key}
                  className="absolute truncate border-l border-line px-2 text-[12px] font-semibold text-ink-2"
                  style={{ left: tick.x, width: tick.width, top: scale.groups.length ? GROUP_H : 0, height: HEADER_H, lineHeight: `${HEADER_H}px` }}
                  data-testid="gantt-tick"
                >
                  {tick.label}
                </div>
              ))}
            </div>
          </div>

          {/* Marker chips */}
          {markerRowH ? (
            <div className="flex border-b border-line" style={{ height: MARKER_H }}>
              <div className="sticky left-0 z-[3] flex shrink-0 items-center border-r border-line bg-surface px-3 text-[12px] text-ink-3" style={{ width }}>
                Markers
              </div>
              <div className="relative shrink-0" style={{ width: scale.width }}>
                {markerList.map((marker) => {
                  // centred on its line when there is room; pushed aside so chips never overlap or leave the axis
                  const left = chipPositions[marker.key]
                  return (
                    <span
                      key={marker.key}
                      className={cn(
                        "absolute top-1/2 z-[2] -translate-y-1/2 truncate whitespace-nowrap rounded-md px-1.5 text-[11.5px] font-bold leading-5",
                        marker.today ? "z-[3] bg-status-red text-white" : cn("border", toneClass(marker.tone || "navy"), "border-[color:var(--tone)] bg-[color:var(--tone-bg)] text-[color:var(--tone-ink)]")
                      )}
                      style={{ left: left.x, maxWidth: left.width }}
                      title={marker.label}
                      data-testid={marker.today ? "gantt-today-chip" : `gantt-marker-${marker.key}`}
                    >
                      {marker.label}
                    </span>
                  )
                })}
              </div>
            </div>
          ) : null}

          {/* Rows */}
          {hasRows ? (
            <div className="relative">
              {layout.lines.map((line) => (line.type === "section" ? (
                <div key={line.key} className="flex border-b border-line" style={{ height: line.height }}>
                  {/* the heading may run past the label column (it is painted over the grey track band) */}
                  <div
                    className="sticky left-0 z-[3] flex shrink-0 items-center gap-2 whitespace-nowrap bg-surface-2 px-3 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3"
                    style={{ width }}
                  >
                    <span className="shrink-0">{line.section.label}</span>
                    {line.section.meta ? <span className="font-medium normal-case tracking-normal">· {line.section.meta}</span> : null}
                  </div>
                  <div className="relative z-[1] shrink-0 bg-surface-2" style={{ width: scale.width }} aria-hidden="true" />
                </div>
              ) : (
                <div
                  key={line.key}
                  role="group"
                  aria-label={line.row.label}
                  className="flex border-b border-line-2 last:border-b-0"
                  style={{ height: line.height }}
                  data-testid={line.row.testId || `gantt-row-${line.row.id}`}
                >
                  <div className="sticky left-0 z-[3] flex shrink-0 items-center border-r border-line bg-surface px-3" style={{ width }}>
                    <RowLabel row={line.row} />
                  </div>
                  <div className="relative shrink-0" style={{ width: scale.width }}>
                    {(line.row.bars || []).map((bar) => {
                      const span = scale.span(bar.start, bar.end)
                      return span ? (
                        <GanttBar key={bar.id} row={line.row} bar={bar} span={span} top={(line.height - barH) / 2} height={barH} onBarClick={onBarClick} />
                      ) : null
                    })}
                    {milestoneLines(line.row, line.height).map(({ center, milestones }) => milestones
                      .map((milestone) => ({ milestone, x: scale.toX(milestone.date) }))
                      .filter((entry) => entry.x !== null)
                      .sort((a, b) => a.x - b.x)
                      .map((entry, index, list) => {
                        const x = entry.x + scale.pxPerDay / 2
                        const next = list[index + 1]
                        // a label never runs over the next diamond on its line (it truncates, or hides below 36px —
                        // the diamond keeps its tooltip and accessible name)
                        const room = next ? next.x + scale.pxPerDay / 2 - x - 18 : Infinity
                        return (
                          <GanttMilestone key={entry.milestone.id} row={line.row} milestone={entry.milestone} x={x} rowHeight={line.height} center={center} labelRoom={room} />
                        )
                      }))}
                    {(line.row.leadChips || []).some(Boolean) ? (
                      // "‹ 2 earlier" at the start of each line, kept in view (sticky) while the chart scrolls
                      <div className="pointer-events-none absolute inset-0 z-[2]">
                        {milestoneLines(line.row, line.height).map(({ center }, index) => (line.row.leadChips[index] ? (
                          <div key={index} className="absolute inset-x-0 flex items-center" style={{ top: center - 10, height: 20 }}>
                            <span className="pointer-events-auto sticky inline-flex" style={{ left: width + 4 }} data-testid={`gantt-lead-${line.row.id}-${index}`}>
                              {line.row.leadChips[index]}
                            </span>
                          </div>
                        ) : null))}
                      </div>
                    ) : null}
                    {!(line.row.bars || []).length && !(line.row.milestones || []).length && line.row.note ? (
                      // sticky: stays readable at the left of the visible timeline while it scrolls sideways
                      <span className="sticky z-[1] inline-flex h-full items-center whitespace-nowrap px-3 text-[12px] text-ink-3" style={{ left: width }}>
                        {line.row.note}
                      </span>
                    ) : null}
                  </div>
                </div>
              )))}

              {/* Grid lines, shaded ranges, today line and markers (under the bars) */}
              <div className="pointer-events-none absolute inset-y-0 z-0" style={{ left: width, width: scale.width }} aria-hidden="true">
                {scale.ticks.map((tick) => (
                  <span key={tick.key} className="absolute inset-y-0 border-l border-line-2" style={{ left: tick.x }} />
                ))}
                {markerList.filter((marker) => marker.shadeTo).map((marker) => {
                  const from = scale.toX(marker.date)
                  const to = scale.toX(marker.shadeTo === "today" ? todayValue : marker.shadeTo)
                  if (from === null || to === null) return null
                  return (
                    <span
                      key={`shade-${marker.key}`}
                      className={cn("absolute inset-y-0 bg-[color:var(--tone-bg)] opacity-60", toneClass(marker.tone || "red"))}
                      style={{ left: Math.min(from, to), width: Math.abs(to - from) }}
                    />
                  )
                })}
                {markerList.map((marker) => (
                  <span
                    key={`line-${marker.key}`}
                    className={cn("absolute inset-y-0", toneClass(marker.tone || "navy"))}
                    style={{
                      left: scale.toX(marker.date) + (marker.today ? scale.pxPerDay / 2 : 0),
                      borderLeft: `${marker.today ? 2 : 1.5}px ${marker.dashed ? "dashed" : "solid"} var(--tone)`,
                    }}
                    data-testid={marker.today ? "gantt-today-line" : undefined}
                  />
                ))}
              </div>

              {/* Dependency arrows (over the bars) */}
              {dependencies.length ? (
                <svg
                  className="pointer-events-none absolute top-0 z-[2] overflow-visible"
                  style={{ left: width }}
                  width={scale.width}
                  height={layout.height}
                  aria-hidden="true"
                  data-testid="gantt-dependencies"
                >
                  <defs>
                    <marker id={`${testId}-arrow`} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                      <path d="M0,0 L8,4 L0,8 z" fill="var(--ink-3)" />
                    </marker>
                    <marker id={`${testId}-arrow-critical`} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                      <path d="M0,0 L8,4 L0,8 z" fill="var(--status-red)" />
                    </marker>
                  </defs>
                  {dependencies.map((dep) => {
                    const from = barBoxes[dep.from]
                    const to = barBoxes[dep.to]
                    if (!from || !to) return null
                    return (
                      <path
                        key={`${dep.from}->${dep.to}`}
                        d={dependencyPath(from, to)}
                        fill="none"
                        stroke={dep.critical ? "var(--status-red)" : "var(--ink-3)"}
                        strokeWidth={dep.critical ? 1.75 : 1.25}
                        markerEnd={`url(#${testId}-arrow${dep.critical ? "-critical" : ""})`}
                        data-critical={dep.critical ? "true" : "false"}
                      />
                    )
                  })}
                </svg>
              ) : null}
            </div>
          ) : (
            <div className="flex">
              <div className="sticky left-0 px-4 py-8 text-[13px] text-ink-3" style={{ width: Math.min(totalWidth, 480) }}>{emptyText}</div>
            </div>
          )}
          <span className="sr-only">{`Time axis from ${formatDayLabel(scale.startDay, { withYear: true })} to ${formatDayLabel(scale.endDay - 1, { withYear: true })}.`}</span>
        </div>
      </div>
    </div>
  )
}

/** The legend under a Gantt: kinds of bars and lines. items: [{ kind | type, label }]. */
const LEGEND_SWATCH = {
  proposed: <span className="ui-gantt-hatch inline-block h-2.5 w-5 rounded-[3px]" />,
  plan: <span className="inline-block h-2.5 w-5 rounded-[3px] border border-[color:var(--brand-soft-border)] bg-brand-soft" />,
  actual: <span className="inline-block h-2.5 w-5 rounded-[3px] bg-brand" />,
  late: <span className="inline-block h-2.5 w-5 rounded-[3px] bg-status-red" />,
  today: <span className="inline-block h-3 w-0.5 bg-status-red" />,
  past: <span className="inline-block h-2.5 w-5 rounded-[3px] bg-status-red-bg" />,
  milestone: <span className="inline-block h-2 w-2 rotate-45 rounded-[1px] bg-[color:var(--status-navy)]" />,
  variation: <span className="inline-block h-2 w-2 rotate-45 rounded-[1px] bg-status-violet" />,
  critical: <span className="inline-block h-0.5 w-5 bg-status-red" />,
}

function GanttLegend({ items, className, children }) {
  return (
    <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-ink-3", className)} data-testid="gantt-legend">
      {(items || []).map((item) => (
        <span key={item.kind || item.label} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-flex items-center">{LEGEND_SWATCH[item.kind] || null}</span>
          {item.label}
        </span>
      ))}
      {children ? <span className="ml-auto">{children}</span> : null}
    </div>
  )
}

export { Gantt, GanttLegend }
